import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Layout } from '@/components/Layout';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { 
  CheckCircle2, XCircle, Settings, FileText, QrCode, Clock, 
  Upload, Shield, Database, AlertCircle 
} from 'lucide-react';

export default function VerifactuAdmin({ user }) {
  const [config, setConfig] = useState({
    activo: false,
    nif: '',
    razon_social: '',
    direccion: '',
    codigo_postal: '',
    municipio: '',
    provincia: '',
    serie_facturas: 'T2025',
    dispositivo_id: 'TPV-01',
    contador_actual: 0,
    iva_por_defecto: 0.10,
    certificado_p12_path: null,
    certificado_password: '',
    aeat_entorno: 'pruebas'
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);
  const [certificateFile, setCertificateFile] = useState(null);
  const [testHash, setTestHash] = useState(null);

  const business_id = user?.business_id || 'default';

  useEffect(() => {
    fetchConfig();
    fetchAudit();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axiosInstance.get(`/verifactu/config/${business_id}`);
      setConfig({
        ...response.data,
        iva_por_defecto: parseFloat(response.data.iva_por_defecto || 0.10)
      });
    } catch (error) {
      toast.error('Error al cargar configuración');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAudit = async () => {
    try {
      const response = await axiosInstance.get(`/verifactu/audit?business_id=${business_id}&limit=50`);
      setAudit(response.data.audit || []);
    } catch (error) {
      console.error('Error loading audit:', error);
    }
  };

  const handleSave = async () => {
    if (!config.nif || !config.razon_social) {
      toast.error('Por favor completa los campos obligatorios (NIF y Razón Social)');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...config,
        business_id: business_id
      };
      
      await axiosInstance.post('/verifactu/config', payload);
      toast.success('Configuración guardada correctamente');
      
      // Si hay certificado pendiente, subirlo
      if (certificateFile) {
        await uploadCertificate();
      }
      
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const uploadCertificate = async () => {
    if (!certificateFile) {
      toast.error('No hay archivo seleccionado');
      return;
    }

    const formData = new FormData();
    formData.append('file', certificateFile);

    try {
      await axiosInstance.post(`/verifactu/upload-certificate/${business_id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('✅ Certificado subido correctamente');
      setCertificateFile(null);
      
      // Limpiar input
      const fileInput = document.getElementById('cert-upload');
      if (fileInput) fileInput.value = '';
      
      await fetchConfig();
    } catch (error) {
      toast.error('Error al subir certificado: ' + (error.response?.data?.detail || error.message));
    }
  };

  const testHashAlgorithm = async () => {
    try {
      const res = await axiosInstance.post(`/verifactu/test-hash?business_id=${business_id}`);
      setTestHash(res.data);
      toast.success('Hash de prueba generado correctamente');
    } catch (error) {
      toast.error('Error al generar hash: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <p>Cargando...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[#212B35]">Verifactu / AEAT</h1>
          <p className="text-gray-600">Configuración de cumplimiento normativo de facturación</p>
        </div>

        {/* Status Alert */}
        <Alert className={config.activo ? 'border-green-500 bg-green-50' : 'border-orange-500 bg-orange-50'}>
          <AlertDescription className="flex items-center gap-2">
            {config.activo ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="text-green-800">Verifactu está ACTIVO - Facturas se envían automáticamente a AEAT</span>
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 text-orange-600" />
                <span className="text-orange-800">Verifactu está DESACTIVADO - Las facturas no se envían a AEAT</span>
              </>
            )}
          </AlertDescription>
        </Alert>

        {/* Configuration Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuración Verifactu
            </CardTitle>
            <CardDescription>
              Configura los datos de tu empresa para el cumplimiento de Verifactu
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
              <div>
                <Label className="text-base font-semibold">Activar Verifactu</Label>
                <p className="text-sm text-gray-600">Enviar facturas automáticamente a la AEAT</p>
              </div>
              <Button
                variant={config.activo ? 'default' : 'outline'}
                onClick={() => setConfig({ ...config, activo: !config.activo })}
                className={config.activo ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                {config.activo ? 'Activado' : 'Desactivado'}
              </Button>
            </div>

            {config.activo && (
              <>
                {/* Datos Fiscales */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Datos Fiscales del Emisor
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nif">NIF/CIF <span className="text-red-500">*</span></Label>
                      <Input
                        id="nif"
                        value={config.nif}
                        onChange={(e) => setConfig({ ...config, nif: e.target.value.toUpperCase() })}
                        placeholder="B12345678"
                        maxLength={9}
                      />
                    </div>

                    <div>
                      <Label htmlFor="razon_social">Razón Social <span className="text-red-500">*</span></Label>
                      <Input
                        id="razon_social"
                        value={config.razon_social}
                        onChange={(e) => setConfig({ ...config, razon_social: e.target.value })}
                        placeholder="Mi Empresa SL"
                      />
                    </div>

                    <div className="col-span-2">
                      <Label htmlFor="direccion">Dirección Fiscal</Label>
                      <Input
                        id="direccion"
                        value={config.direccion}
                        onChange={(e) => setConfig({ ...config, direccion: e.target.value })}
                        placeholder="C/ Ejemplo 1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="codigo_postal">Código Postal</Label>
                      <Input
                        id="codigo_postal"
                        value={config.codigo_postal}
                        onChange={(e) => setConfig({ ...config, codigo_postal: e.target.value })}
                        placeholder="28001"
                      />
                    </div>

                    <div>
                      <Label htmlFor="municipio">Municipio</Label>
                      <Input
                        id="municipio"
                        value={config.municipio}
                        onChange={(e) => setConfig({ ...config, municipio: e.target.value })}
                        placeholder="Madrid"
                      />
                    </div>

                    <div>
                      <Label htmlFor="provincia">Provincia</Label>
                      <Input
                        id="provincia"
                        value={config.provincia}
                        onChange={(e) => setConfig({ ...config, provincia: e.target.value })}
                        placeholder="Madrid"
                      />
                    </div>
                  </div>
                </div>

                {/* Numeración */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    Numeración de Facturas
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="serie">Serie de Facturas</Label>
                      <Input
                        id="serie"
                        value={config.serie_facturas}
                        onChange={(e) => setConfig({ ...config, serie_facturas: e.target.value })}
                        placeholder="T2025"
                      />
                    </div>

                    <div>
                      <Label htmlFor="dispositivo">ID Dispositivo TPV</Label>
                      <Input
                        id="dispositivo"
                        value={config.dispositivo_id}
                        onChange={(e) => setConfig({ ...config, dispositivo_id: e.target.value })}
                        placeholder="TPV-01"
                      />
                    </div>

                    <div>
                      <Label htmlFor="contador">Contador Actual</Label>
                      <Input
                        id="contador"
                        type="number"
                        value={config.contador_actual}
                        disabled
                        className="bg-gray-100"
                      />
                      <p className="text-xs text-gray-500 mt-1">Autogenerado</p>
                    </div>
                  </div>
                </div>

                {/* IVA por Defecto */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">📊 IVA por Defecto</h3>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex gap-3 mb-3">
                      <AlertCircle className="text-yellow-600 flex-shrink-0" size={20} />
                      <div className="text-sm text-yellow-800">
                        <p className="font-semibold mb-1">Tipo de IVA aplicable</p>
                        <p>Este es el tipo de IVA que se aplicará por defecto en las facturas.</p>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="iva">Tipo de IVA (%)</Label>
                      <Select 
                        value={config.iva_por_defecto.toString()} 
                        onValueChange={(value) => {
                          const newValue = parseFloat(value);
                          setConfig({...config, iva_por_defecto: newValue});
                          toast.info(`IVA cambiado a ${(newValue * 100).toFixed(0)}%`);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0.04">4% - Superreducido</SelectItem>
                          <SelectItem value="0.10">10% - Reducido (Hostelería) ⭐</SelectItem>
                          <SelectItem value="0.21">21% - General</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-emerald-600 mt-2 font-semibold">
                        ✓ Valor actual: {(config.iva_por_defecto * 100).toFixed(0)}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Certificado Digital */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Certificado Digital (.p12 / .pfx)
                  </h3>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    {/* Certificado cargado */}
                    {config.certificado_p12_path && !certificateFile && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="text-emerald-500" size={24} />
                            <div>
                              <p className="text-gray-900 font-medium">✅ Certificado cargado</p>
                              <p className="text-sm text-gray-500">{config.certificado_p12_path}</p>
                            </div>
                          </div>
                          <Button
                            onClick={() => document.getElementById('cert-upload').click()}
                            variant="outline"
                          >
                            Cambiar
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Archivo seleccionado */}
                    {certificateFile && (
                      <div className="space-y-3">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <AlertCircle className="text-yellow-600" size={24} />
                              <div>
                                <p className="text-gray-900 font-medium">📄 Archivo seleccionado</p>
                                <p className="text-sm text-yellow-700">{certificateFile.name}</p>
                                <p className="text-xs text-yellow-600">
                                  Tamaño: {(certificateFile.size / 1024).toFixed(2)} KB
                                </p>
                              </div>
                            </div>
                            <Button
                              onClick={() => {
                                setCertificateFile(null);
                                document.getElementById('cert-upload').value = '';
                              }}
                              variant="destructive"
                              size="sm"
                            >
                              Quitar
                            </Button>
                          </div>
                          <Button
                            onClick={uploadCertificate}
                            disabled={loading}
                            className="w-full bg-emerald-600 hover:bg-emerald-700"
                          >
                            {loading ? '⏳ Subiendo...' : '⬆️ Subir Certificado Ahora'}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Sin certificado */}
                    {!config.certificado_p12_path && !certificateFile && (
                      <div className="text-center">
                        <Upload className="mx-auto text-gray-400 mb-3" size={40} />
                        <p className="text-gray-900 mb-2">Sube tu certificado digital</p>
                        <p className="text-sm text-gray-500 mb-4">Archivo .p12 o .pfx</p>
                        <Button
                          onClick={() => document.getElementById('cert-upload').click()}
                          variant="outline"
                        >
                          📂 Seleccionar archivo
                        </Button>
                      </div>
                    )}
                    
                    <input
                      id="cert-upload"
                      type="file"
                      accept=".p12,.pfx"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setCertificateFile(file);
                          toast.info(`Archivo seleccionado: ${file.name}`);
                        }
                      }}
                      className="hidden"
                    />
                  </div>

                  <div>
                    <Label htmlFor="cert-password">
                      Contraseña del Certificado <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="cert-password"
                      type="password"
                      value={config.certificado_password}
                      onChange={(e) => setConfig({...config, certificado_password: e.target.value})}
                      placeholder="••••••••"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Esta contraseña se guarda al hacer click en "Guardar Configuración"
                    </p>
                  </div>
                </div>

                {/* Entorno AEAT */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Configuración de Entorno
                  </h3>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800 mb-3">
                      La autenticación con AEAT se realiza mediante el certificado digital.
                    </p>
                    
                    <div>
                      <Label htmlFor="entorno">Entorno de Remisión</Label>
                      <Select 
                        value={config.aeat_entorno} 
                        onValueChange={(value) => setConfig({...config, aeat_entorno: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pruebas">🧪 Pruebas (Recomendado)</SelectItem>
                          <SelectItem value="produccion">🚀 Producción (Oficial)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-600 mt-2">
                        {config.aeat_entorno === 'pruebas' 
                          ? '✅ Usando entorno de pruebas de AEAT' 
                          : '⚠️ PRODUCCIÓN: Los registros tienen validez legal'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Test de Hash */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">Test de Algoritmo de Hash</h4>
                    <Button
                      onClick={testHashAlgorithm}
                      variant="outline"
                      size="sm"
                    >
                      Ejecutar Test
                    </Button>
                  </div>
                  
                  {testHash && (
                    <div className="bg-gray-900 rounded p-3 font-mono text-xs">
                      <p className="text-gray-400">Hash generado:</p>
                      <p className="text-emerald-400 break-all">{testHash.hash}</p>
                      <div className="flex gap-4 mt-2">
                        <span className="text-gray-400">Longitud: {testHash.longitud}</span>
                        <span className={testHash.valido ? 'text-emerald-400' : 'text-red-400'}>
                          {testHash.valido ? '✓ Válido' : '✗ Inválido'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Botones de acción */}
            <div className="flex gap-4 pt-4 border-t">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-[#9C0F17] hover:bg-[#7A0C12]"
              >
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
              
              <Button
                onClick={fetchConfig}
                variant="outline"
              >
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Audit Log */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Auditoría Reciente
            </CardTitle>
            <CardDescription>Últimas 50 operaciones de Verifactu</CardDescription>
          </CardHeader>
          <CardContent>
            {audit.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No hay registros aún</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left">Fecha</th>
                      <th className="p-2 text-left">Factura</th>
                      <th className="p-2 text-left">Operación</th>
                      <th className="p-2 text-left">Estado</th>
                      <th className="p-2 text-left">Detalles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map((log, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                        </td>
                        <td className="p-2">{log.invoice_number || log.invoice_id?.substring(0, 8)}</td>
                        <td className="p-2">{log.operation || 'send'}</td>
                        <td className="p-2">
                          {log.status === 'success' ? (
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Éxito
                            </span>
                          ) : (
                            <span className="text-red-600 flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              Error
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-xs text-gray-600">
                          {log.message || log.error || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
